// @ts-nocheck
// Ported byte-identically from v1 views/dashboard/screens/LeadsScreen.js.
// The agent's Leads pane — lists pending/completed lead assignments,
// grouped by assignment, with a stacked completion bar per group and
// KPI cards + donut + horizontal-bar summary above.
// Behavior preserved:
//   - useActiveLoginType hook mirrors sessionStorage.activeLoginType across
//     tabs (storage) and same-tab custom events (sessionStorageChange).
//   - canCall = activeLoginType present AND no dispositionActive AND
//     isOutboundEnabled. Call button disabled + specific toast when blocked.
//   - `team_lead` role puts the screen into read-only mode (badge column
//     instead of Call button, extra "Completed At" column, alt copy).
//   - Debounced fetch on date change (400ms).
//   - Date defaults: fromDate = first day of current month, toDate = today.
//   - Search (contact_name/contact_number) and status filter run on the
//     server per assignment, one page at a time (an assignment can hold 50K
//     leads); KPIs/charts come from the per-assignment counts. Name filter
//     dropdown stays client-side over the assignment list.
// Chrome: v1 uses react-bootstrap directly — kept as-is (no PrimeReact
// components involved). Chart is react-apexcharts, already installed.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { downloadCsv, stampedFileName } from '@/components/data-table/csv-export';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall } from '@/service/httpCall';
import { API_AGENT_LEADS_MY_ASSIGNMENTS, API_AGENT_LEADS_MY_ITEMS } from '@/service/url';
import { LeadsPager, usePagedLeads } from '@/features/agent-leads/usePagedLeads';
import { getUser } from '@/utilities/setting';
import { getAgentMasking, maskNumber } from '@/utilities/cliDisplay';
import { FaPhone } from 'react-icons/fa';
import Chart from 'react-apexcharts';
import { useCallStore } from '@/router/zustand/callStore';
// Dashboard-mode only (isDateControlled) — reused so the KPI/chart cards
// match the rest of My Dashboard instead of this screen's own Bootstrap
// cards. Not used, and not imported for, any other mount of this screen.
import StatTile from '@/features/cti/components/call-stats/StatTile';
import ChartCard from '@/features/cti/components/call-stats/ChartCard';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

function useActiveLoginType() {
  const [activeLoginType, setActiveLoginType] = useState(
    typeof window !== 'undefined'
      ? sessionStorage.getItem('activeLoginType')
      : null
  );

  useEffect(() => {
    const sync = () => {
      const current = sessionStorage.getItem('activeLoginType');
      setActiveLoginType((prev) => (prev !== current ? current : prev));
    };

    const handleStorageChange = (e) => {
      if (e.key === 'activeLoginType') setActiveLoginType(e.newValue);
    };
    const handleCustomChange = (e) => {
      if (e.detail?.key === 'activeLoginType') setActiveLoginType(e.detail.value);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('sessionStorageChange', handleCustomChange);
    sync();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sessionStorageChange', handleCustomChange);
    };
  }, []);

  return activeLoginType;
}

const PAGE_SIZE = 25;
// Export pages the same items endpoint (server caps limit at 100), one request
// at a time, and stops at a hard row cap so one click can't scan 50K-lead
// assignments. ponytail: client-side paging, add a streamed export endpoint if
// agents need more than EXPORT_MAX_ROWS in one file.
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_ROWS = 10000;
const exportTime = (v) => (v ? `="${new Date(v).toLocaleString('sv-SE')}"` : '-');
const exportFieldValue = (v) => {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.map(exportFieldValue).filter(Boolean).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};
// Form answers can hold the customer's number (e.g. "Contact Number"): mask phone-like runs for masking agents.
const PHONE_RUN = /\+?\d{10,13}/g;
const exportSubmission = (data, mask = false) =>
  data && typeof data === 'object'
    ? Object.entries(data)
        .map(([k, v]) => {
          const value = exportFieldValue(v);
          return [String(k).replace(/[_-]+/g, ' ').trim(), mask ? value.replace(PHONE_RUN, (m) => maskNumber(m)) : value];
        })
        .filter(([, v]) => v !== '')
        .map(([k, v]) => `${k}: ${v}`)
        .join('; ')
    : '';

function PagedGroupLeads({ group, filters, reloadKey, children }: any) {
  const paged = usePagedLeads(
    API_AGENT_LEADS_MY_ITEMS.replace(':assignmentId', group.assignment_id),
    filters,
    PAGE_SIZE,
  );
  const { reload } = paged;
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    reload();
  }, [reloadKey]);
  return (
    <>
      {children(paged.rows, (paged.page - 1) * paged.limit, paged.loading)}
      <LeadsPager page={paged.page} total={paged.total} limit={paged.limit} onPage={paged.setPage} />
    </>
  );
}

export default function LeadsScreen({
  onCallLead,
  onClose,
  active,
  isOutboundEnabled = true,
  onPendingCountChange,
  // Controlled mode — AgentDashboardView.tsx's page-level filter drives the
  // date range and portals the search/status controls into its own shared
  // bar instead of this screen's own row. Every field is optional and
  // undefined by default, so every other caller (IdleScreen.tsx,
  // CTIAgentUI.tsx) is byte-identical to before.
  fromDate: fromDateProp,
  toDate: toDateProp,
  statusFilter: statusFilterProp,
  onStatusFilterChange,
  searchTerm: searchTermProp,
  onSearchTermChange,
  filterPortalTarget,
}: any) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<any[]>([]);

  const isReadOnly = getUser()?.agentData?.access === 'team_lead';
  const maskNumbers = getAgentMasking(getUser());
  const displayNumber = (num) => (maskNumbers ? maskNumber(num) : num);

  const activeLoginType = useActiveLoginType();
  const dispositionActive = useCallStore((state) => state.dispositionActive);
  const autoDialNextLead = useCallStore((state) => state.autoDialNextLead);
  const leadsRefreshToken = useCallStore((state) => state.leadsRefreshToken);
  const autoDialQueued = Boolean(autoDialNextLead);
  const canCall = Boolean(activeLoginType) && !dispositionActive && isOutboundEnabled && !autoDialQueued;
  const isOnBreak = useCallStore((state) => state.isOnBreak);
  const callState = useCallStore((state) => state.callState);
  const isPlacingCall = useCallStore((state) => state.isPlacingCall);
  const dispositionOpen = useCallStore((state) => state.dispositionOpen);
  const recallBlockedReason = !isOutboundEnabled
    ? 'Outbound calling is disabled for this agent.'
    : !activeLoginType
      ? 'Login to take calls first.'
      : isOnBreak
        ? 'End your break before calling.'
        : (callState && callState !== 'idle') || isPlacingCall
          ? 'End your current call before calling.'
          : dispositionActive || dispositionOpen
            ? 'Finish the active disposition before calling.'
            : autoDialQueued
              ? 'The next lead is being dialled — wait for that call.'
              : null;

  const todayStr = (() => {
    const d = new Date();
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
  })();

  const firstDayOfMonthStr = (() => {
    const d = new Date();
    const off = d.getTimezoneOffset();

    const localDate = new Date(d.getTime() - off * 60000);
    localDate.setDate(1);

    return localDate.toISOString().slice(0, 10);
  })();

  const [fromDateState, setFromDateState] = useState(firstDayOfMonthStr);
  const [toDateState, setToDateState] = useState(todayStr);
  const [searchTermState, setSearchTermState] = useState('');
  const [statusFilterState, setStatusFilterState] = useState('all');
  const [leadFilter, setLeadFilter] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);

  const isDateControlled = fromDateProp !== undefined;
  const fromDate = isDateControlled ? fromDateProp : fromDateState;
  const toDate = isDateControlled ? toDateProp : toDateState;
  const setFromDate = isDateControlled ? () => {} : setFromDateState;
  const setToDate = isDateControlled ? () => {} : setToDateState;

  const isFilterControlled = filterPortalTarget !== undefined;
  const searchTerm = isFilterControlled ? (searchTermProp || '') : searchTermState;
  const statusFilter = isFilterControlled ? (statusFilterProp || 'all') : statusFilterState;
  const setSearchTerm = isFilterControlled ? (onSearchTermChange || (() => {})) : setSearchTermState;
  const setStatusFilter = isFilterControlled ? (onStatusFilterChange || (() => {})) : setStatusFilterState;

  const fetchLeads = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    const qs = params.toString();
    getApiCall(`${API_AGENT_LEADS_MY_ASSIGNMENTS}${qs ? `?${qs}` : ''}`)
      .then((resp: any) => setGroups(Array.isArray(resp) ? resp : []))
      .catch((err) => toast.error(friendlyApiError(err) || 'Unable to load leads'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!active) return undefined;
    const t = setTimeout(fetchLeads, 400);
    return () => clearTimeout(t);
  }, [active, fromDate, toDate, leadsRefreshToken]);

  const refresh = () => {
    fetchLeads();
    setRefreshKey((k) => k + 1);
  };

  const clearDateFilter = () => {
    setFromDate('');
    setToDate('');
  };

  const isCompleted = (lead) => String(lead?.status || '').toLowerCase() === 'completed';

  const pendingOf = (g) => Number(g.pending_count) || 0;
  const completedOf = (g) => Number(g.completed_count) || 0;
  const itemFilters = { status: statusFilter, search: searchTerm, from_date: fromDate, to_date: toDate };

  const groupName = (g) => g.title || g.contact_group || 'Leads';
  const leadNames = [...new Set(groups.map(groupName))];

  const LeadProgress = ({ group }: any) => {
    const completed = completedOf(group);
    const pending = pendingOf(group);
    const total = completed + pending;
    const pct = (n) => (total ? (n / total) * 100 : 0);
    return (
      <div className="mb-2">
        <div
          style={{
            display: 'flex',
            width: '100%',
            height: 28,
            borderRadius: 6,
            overflow: 'hidden',
            background: '#e9ecef',
          }}
          title={`${completed} completed / ${pending} pending of ${total}`}
        >
          {completed > 0 && (
            <div
              style={{
                width: `${pct(completed)}%`,
                background: '#16a34a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 16,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {pct(completed) >= 28
                ? `Completed: ${completed}`
                : pct(completed) >= 8
                  ? completed
                  : ''}
            </div>
          )}
          {pending > 0 && (
            <div
              style={{
                width: `${pct(pending)}%`,
                background: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 16,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {pct(pending) >= 28
                ? `Pending: ${pending}`
                : pct(pending) >= 8
                  ? pending
                  : ''}
            </div>
          )}
        </div>
        <div className="d-flex gap-3 mt-1" style={{ fontSize: 17 }}>
          <span>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                background: '#16a34a',
                borderRadius: 2,
                marginRight: 4,
              }}
            />
            Completed: <strong>{completed}</strong>
          </span>
          <span>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                background: '#dc2626',
                borderRadius: 2,
                marginRight: 4,
              }}
            />
            Pending: <strong>{pending}</strong>
          </span>
          <span className="text-muted">Total: {total}</span>
        </div>
      </div>
    );
  };

  const isFiltering =
    statusFilter !== 'all' || searchTerm.trim() !== '' || leadFilter !== 'all';

  const displayGroups = groups
    .filter((g) => leadFilter === 'all' || groupName(g) === leadFilter)
    .filter((g) =>
      statusFilter === 'pending' ? pendingOf(g) > 0 : statusFilter === 'completed' ? completedOf(g) > 0 : true
    );

  // Exports what the list shows: the visible assignments, with the same
  // status/search/date filters the per-assignment tables send to the server.
  const exportLeads = async () => {
    if (exporting) return;
    if (!displayGroups.length) {
      toast.info('No leads to export');
      return;
    }
    setExporting(true);
    try {
      const base = new URLSearchParams({ limit: String(EXPORT_PAGE_SIZE) });
      if (statusFilter && statusFilter !== 'all') base.set('status', statusFilter);
      if (searchTerm.trim()) base.set('search', searchTerm.trim());
      if (fromDate) base.set('from_date', fromDate);
      if (toDate) base.set('to_date', toDate);
      const picked: [any, any][] = [];
      let truncated = false;
      groups: for (const g of displayGroups) {
        const url = API_AGENT_LEADS_MY_ITEMS.replace(':assignmentId', g.assignment_id);
        for (let page = 1; ; page++) {
          if (picked.length >= EXPORT_MAX_ROWS) {
            truncated = true;
            break groups;
          }
          base.set('page', String(page));
          const resp: any = await getApiCall(`${url}?${base}`, {});
          const rows = Array.isArray(resp?.rows) ? resp.rows : [];
          rows.forEach((it) => picked.push([g, it]));
          if (rows.length < EXPORT_PAGE_SIZE || page * EXPORT_PAGE_SIZE >= (Number(resp?.total) || 0)) break;
        }
      }
      if (!picked.length) {
        toast.info('No leads match your search/filter.');
        return;
      }
      const text = (v) => (v === null || v === undefined || v === '' ? '-' : String(v));
      const header = [
        '#', 'Assignment Title', 'Contact Group', 'Disposition Form', 'Assigned By', 'Assigned At',
        'Transferred By', 'Transferred At', 'Contact Name', 'Number', 'Status', isReadOnly ? 'Agent (Completed By)' : 'Completed By',
        'Completed At', 'Disposition Details',
      ];
      const lines = picked.map(([g, it], i) => {
        const num = it.contact_number == null ? '' : String(displayNumber(it.contact_number));
        return [
          i + 1,
          text(groupName(g)),
          text(g.contact_group),
          text(g.disposition_form?.name),
          text(g.assigned_by),
          exportTime(g.assigned_at),
          text(g.transferred_at ? g.transferred_by : null),
          exportTime(g.transferred_at),
          text(it.contact_name),
          num ? `="${num}"` : '-',
          isCompleted(it) ? 'Completed' : 'Pending',
          text(it.completed_by),
          exportTime(it.completed_at),
          text(exportSubmission(it.submission_data, maskNumbers)),
        ];
      });
      downloadCsv([header, ...lines], stampedFileName('leads'));
      if (truncated) {
        toast.warning(
          `Exported the first ${EXPORT_MAX_ROWS.toLocaleString('en-IN')} leads. Narrow the filters to export the rest.`,
        );
      }
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Unable to export leads');
    } finally {
      setExporting(false);
    }
  };

  const totalPending = groups.reduce((sum, g) => sum + pendingOf(g), 0);

  // Optional — undefined for every other caller of this screen, so this is
  // additive only. AgentDashboardView.tsx uses it to badge the "Leads" nav
  // pill with a live pending count.
  useEffect(() => {
    onPendingCountChange?.(totalPending);
  }, [totalPending, onPendingCountChange]);

  const totalCompleted = groups.reduce((sum, g) => sum + completedOf(g), 0);
  const totalLeads = totalPending + totalCompleted;
  const completionRate = totalLeads
    ? Math.round((totalCompleted / totalLeads) * 100)
    : 0;

  const donutOptions: any = {
    chart: { type: 'donut' },
    labels: ['Completed', 'Pending'],
    colors: ['#16a34a', '#dc2626'],
    legend: { position: 'bottom' },
    dataLabels: { enabled: true },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              formatter: () => String(totalLeads),
            },
          },
        },
      },
    },
    noData: { text: 'No leads' },
  };
  const donutSeries = [totalCompleted, totalPending];

  const barCategories = groups.map(
    (g) => g.title || g.contact_group || `Assignment ${g.assignment_id}`
  );
  const barOptions: any = {
    chart: { type: 'bar', stacked: true, toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, borderRadius: 4, columnWidth: '45%' } },
    colors: ['#16a34a', '#dc2626'],
    xaxis: { categories: barCategories },
    legend: { position: 'bottom' },
    dataLabels: { enabled: false },
    noData: { text: 'No leads' },
  };
  const barSeries = [
    {
      name: 'Completed',
      data: groups.map(completedOf),
    },
    {
      name: 'Pending',
      data: groups.map(pendingOf),
    },
  ];

  const TableRoot = isDateControlled ? Table : 'table';
  const HeadTag = isDateControlled ? TableHeader : 'thead';
  const BodyTag = isDateControlled ? TableBody : 'tbody';
  const RowTag = isDateControlled ? TableRow : 'tr';
  const HeadCell = isDateControlled ? TableHead : 'th';
  const Cell = isDateControlled ? TableCell : 'td';
  const endAlignClass = isDateControlled ? 'text-right' : 'text-end';
  const mutedRowClass = isDateControlled ? 'text-muted-foreground' : 'text-muted';

  return (
    <div
      className="cti-leads-screen"
      data-dashboard={isDateControlled ? 'true' : undefined}
      style={{ padding: '14px 20px', margin: '0 auto' }}
    >
      {!isDateControlled && (
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
          <div>
            <h4 className="mb-0">{isReadOnly ? 'Team Leads' : 'My Leads'}</h4>
            <small className="text-muted">
              {totalPending} pending lead(s){' '}
              {isReadOnly ? 'in your call group(s)' : 'assigned to you'}
            </small>
          </div>
        </div>
      )}

      {!isReadOnly && !canCall && (
        isDateControlled ? (
          <div className="mb-3 rounded-xl border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-foreground">
            You are not logged in to take calls. Use{' '}
            <strong>“Login to take calls”</strong> in the header to enable calling.
          </div>
        ) : (
          <div className="alert alert-warning py-2 px-3 mb-3" role="alert">
            You are not logged in to take calls. Use{' '}
            <strong>“Login to take calls”</strong> in the header to enable calling.
          </div>
        )
      )}

      {!loading && groups.length > 0 && (
        isDateControlled ? (
          <div className="mb-3 flex items-stretch gap-2.5">
            <div className="grid flex-1 grid-cols-2 gap-2.5 sm:grid-cols-4">
              <StatTile label="Total Leads" value={totalLeads} />
              <StatTile label="Pending" value={totalPending} tone="warn" />
              <StatTile label="Completed" value={totalCompleted} tone="good" />
              <StatTile label="Completion Rate" value={`${completionRate}%`} />
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-auto w-10 shrink-0 rounded-xl"
              onClick={exportLeads}
              disabled={exporting || displayGroups.length === 0}
              title="Export the leads shown (current filters) as CSV"
              aria-label="Export leads"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            </Button>
          </div>
        ) : (
          <div className="row g-3 mb-3">
            <div className="col-6 col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body py-3">
                  <div className="text-muted small">Total Leads</div>
                  <h3 className="mb-0">{totalLeads}</h3>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body py-3">
                  <div className="text-muted small">Pending</div>
                  <h3 className="mb-0 text-info">{totalPending}</h3>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body py-3">
                  <div className="text-muted small">Completed</div>
                  <h3 className="mb-0 text-success">{totalCompleted}</h3>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body py-3">
                  <div className="text-muted small">Completion Rate</div>
                  <h3 className="mb-0">{completionRate}%</h3>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {!isFilterControlled ? (
        <>
          <div className="mb-8" />
          <div
            className="d-flex gap-2 align-items-center flex-wrap sticky top-10 z-[5] -mx-8 border-b border-border/70 bg-card/95 px-8 py-3 backdrop-blur"
            style={{ marginBottom: '20px' }}
          >
            <div className="d-flex align-items-center gap-1">
              <label className="text-muted small mb-0">From</label>
              <CalendarDateInput
                value={fromDate}
                onChange={(e) => setFromDate(e)}
                max={toDate || undefined}
                className="form-control form-control-sm"
                style={{ width: 150 }}
              />
            </div>
            <div className="d-flex align-items-center gap-1">
              <label className="text-muted small mb-0">To</label>
              <CalendarDateInput
                value={toDate}
                onChange={(e) => setToDate(e)}
                min={fromDate || undefined}
                className="form-control form-control-sm"
                style={{ width: 150 }}
              />
            </div>
            {(fromDate || toDate) && (
              <Button variant="outline" size="sm" onClick={clearDateFilter} disabled={loading}>
                Clear
              </Button>
            )}
            <input
              type="search"
              className="form-control form-control-sm"
              style={{ width: 200 }}
              placeholder="Search name or number…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="btn-group btn-group-sm" role="group" aria-label="Status filter">
              {['all', 'pending', 'completed'].map((s) => (
                <Button
                  key={s}
                  variant={statusFilter === s ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter(s)}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </Button>
              ))}
            </div>
            {leadNames.length > 0 && (
              <select
                className="form-select form-select-sm"
                style={{ width: 200 }}
                value={leadFilter}
                onChange={(e) => setLeadFilter(e.target.value)}
                title="Filter by lead name"
              >
                <option value="all">All leads ({leadNames.length})</option>
                {leadNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            )}
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
              ↻ Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportLeads}
              disabled={loading || exporting || displayGroups.length === 0}
              title="Export the leads shown (current filters) as CSV"
            >
              {exporting ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />}
              Export
            </Button>
            {/* Close button removed — Leads is a tab, not a modal. The
             * workspace tab strip at the top of the CTI shell handles
             * open/close via toggle. */}
          </div>
        </>
      ) : (
        filterPortalTarget &&
        createPortal(
          <>
            <input
              type="search"
              className="h-8 rounded-full border border-border bg-muted/60 px-3 text-[11px] outline-none"
              style={{ width: 180 }}
              placeholder="Search name or number…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList className="h-8 rounded-full bg-muted/60 p-1">
                {['all', 'pending', 'completed'].map((s) => (
                  <TabsTrigger
                    key={s}
                    value={s}
                    className="rounded-full px-3 py-0.5 text-[11px] font-medium data-[state=active]:shadow-sm"
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="flex h-8 items-center gap-1 rounded-full border border-border bg-muted/60 px-3 text-[11px] font-medium outline-none transition-colors hover:bg-muted disabled:opacity-50"
              title="Reload leads"
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </>,
          filterPortalTarget,
        )
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : displayGroups.length === 0 ? (
        <div className="text-center text-muted py-5">
          {isFiltering
            ? 'No leads match your search/filter.'
            : isReadOnly
              ? 'No leads assigned to your call group(s) right now.'
              : 'No leads assigned to you right now.'}
        </div>
      ) : (
        displayGroups.map((group: any) => (
          <div key={group.assignment_id} className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
              <div>
                <h6 className="mb-0">{group.title || group.contact_group || 'Leads'}</h6>
                <small className="text-muted">
                  Disposition Form: <strong>{group.disposition_form?.name || '—'}</strong>
                  &nbsp;|&nbsp; Assigned By: <strong>{group.assigned_by || '—'}</strong>
                  &nbsp;|&nbsp; Assigned At:{' '}
                  <strong>{group.assigned_at ? new Date(group.assigned_at).toLocaleString() : '—'}</strong>
                </small>
                {group.transferred_at && (
                  <div className="small text-info mt-1">
                    🔁 Transferred to {isReadOnly ? 'your call group' : 'you'} on{' '}
                    <strong>{new Date(group.transferred_at).toLocaleString()}</strong>
                    {group.transferred_by ? (
                      <>
                        {' '}
                        by <strong>{group.transferred_by}</strong>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
              <Badge className="bg-red-600 text-white hover:bg-red-600">
                {pendingOf(group).toLocaleString('en-IN')} pending
              </Badge>
            </div>
            <PagedGroupLeads group={group} filters={itemFilters} reloadKey={`${leadsRefreshToken}-${refreshKey}`}>
            {(rows, offset, rowsLoading) => (
            <div
              className={isDateControlled ? 'max-h-[min(420px,60vh)] overflow-auto rounded-xl border border-border' : 'table-responsive'}
              style={isDateControlled ? undefined : { maxHeight: 'min(420px, 60vh)', overflowY: 'auto' }}
            >
              <TableRoot
                className={isDateControlled ? undefined : 'table table-striped table-hover mb-0'}
                containerClassName={isDateControlled ? 'overflow-visible' : undefined}
              >
                <HeadTag className="sticky top-0 z-[1] bg-card">
                  <RowTag>
                    <HeadCell style={{ width: 60 }}>#</HeadCell>
                    <HeadCell>Contact Name</HeadCell>
                    <HeadCell>Number to Call</HeadCell>
                    <HeadCell>Completed By</HeadCell>
                    {isReadOnly && <HeadCell>Completed At</HeadCell>}
                    <HeadCell className={endAlignClass}>{isReadOnly ? 'Status' : 'Action'}</HeadCell>
                  </RowTag>
                </HeadTag>
                <BodyTag>
                  {rows.length === 0 && (
                    <RowTag>
                      <Cell colSpan={isReadOnly ? 6 : 5} className={mutedRowClass}>
                        {rowsLoading ? 'Loading…' : isFiltering ? 'No leads match your search/filter.' : 'No leads'}
                      </Cell>
                    </RowTag>
                  )}
                  {rows.map((lead: any, idx: number) => {
                    const done = isCompleted(lead);
                    return (
                      <RowTag key={lead.id} className={done ? mutedRowClass : undefined}>
                        <Cell>{offset + idx + 1}</Cell>
                        <Cell>{lead.contact_name || '—'}</Cell>
                        <Cell>
                          {done && !isReadOnly ? (
                            <span className="inline-flex items-center gap-2">
                              {displayNumber(lead.contact_number)}
                              <Button
                                size="sm"
                                className="h-7 w-7 bg-sky-500 p-0 text-white hover:bg-sky-500"
                                disabled={Boolean(recallBlockedReason)}
                                title={recallBlockedReason || 'Call this lead again'}
                                aria-label="Call this lead again"
                                onClick={() => {
                                  if (recallBlockedReason) {
                                    toast.warning(recallBlockedReason);
                                    return;
                                  }
                                  onCallLead?.(lead, group, { recall: true });
                                }}
                              >
                                <FaPhone size={12} color="white" style={{ transform: 'rotate(90deg)' }} />
                              </Button>
                            </span>
                          ) : (
                            displayNumber(lead.contact_number)
                          )}
                        </Cell>
                        <Cell>{lead.completed_by || '—'}</Cell>
                        {isReadOnly && (
                          <Cell>
                            {lead.completed_at
                              ? new Date(lead.completed_at).toLocaleString()
                              : '—'}
                          </Cell>
                        )}
                        <Cell className={endAlignClass}>
                          {isReadOnly ? (
                            <Badge
                              className={
                                done
                                  ? 'bg-green-600 text-white hover:bg-green-600'
                                  : 'bg-red-600 text-white hover:bg-red-600'
                              }
                            >
                              {done ? 'Completed' : 'Pending'}
                            </Badge>
                          ) : done ? (
                            <Badge className="bg-green-600 text-white hover:bg-green-600">
                              Completed
                            </Badge>
                          ) : (
                            <Button
                              size="sm"
                              className="bg-sky-500 text-white hover:bg-sky-500"
                              disabled={!canCall}
                              title={
                                canCall
                                  ? undefined
                                  : !isOutboundEnabled
                                    ? 'Outbound calling is disabled for this agent'
                                    : dispositionActive
                                      ? 'Finish the active disposition first'
                                      : autoDialQueued
                                        ? 'The next lead is being dialled — wait for that call'
                                        : 'Login to take calls first'
                              }
                              onClick={() => {
                                if (!isOutboundEnabled) {
                                  toast.error('Outbound calling is disabled for this agent.');
                                  return;
                                }
                                if (dispositionActive) {
                                  toast.warning('Finish the active disposition before calling.');
                                  return;
                                }
                                if (!canCall) {
                                  toast.warning('Login to take calls first.');
                                  return;
                                }
                                onCallLead?.(lead, group);
                              }}
                            >
                              {autoDialQueued && String(autoDialNextLead?.leadId) === String(lead.id) ? (
                                <span className="text-xs font-semibold tabular-nums">
                                  Calling in {autoDialNextLead?.secondsLeft}s
                                </span>
                              ) : (
                                <FaPhone size={14} color="white" style={{ transform: 'rotate(90deg)' }} />
                              )}
                            </Button>
                          )}
                        </Cell>
                      </RowTag>
                    );
                  })}
                </BodyTag>
              </TableRoot>
            </div>
            )}
            </PagedGroupLeads>
            <LeadProgress group={group} />
          </div>
        ))
      )}

      {!loading && groups.length > 0 && (
        isDateControlled ? (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <ChartCard title="Pending vs Completed" eyebrow="Leads">
              <Chart options={donutOptions} series={donutSeries} type="donut" height={240} />
            </ChartCard>
            <ChartCard title="Leads by Assignment" eyebrow="Leads">
              <Chart options={barOptions} series={barSeries} type="bar" height={240} />
            </ChartCard>
          </div>
        ) : (
          <div className="row g-3">
            <div className="col-12 col-lg-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <h6 className="mb-2">Pending vs Completed</h6>
                  <Chart
                    options={donutOptions}
                    series={donutSeries}
                    type="donut"
                    height={260}
                  />
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-8">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <h6 className="mb-2">Leads by Assignment</h6>
                  <Chart
                    options={barOptions}
                    series={barSeries}
                    type="bar"
                    height={260}
                  />
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
}
